import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { PageHeader } from '@/components/admin/AdminShell';
import { ListTable, type Column, type ListState } from '@/components/admin/ListTable';
import { Notice } from '@/components/admin/Notice';
import type { OfficeAccount } from '@/features/officeAccounts/api';
import {
  emergencyChangeGrade,
  emergencyChangeStatus,
  fetchOfficeAccountsForSuperAdmin,
  resetOfficeTotp,
} from '@/features/superadmin/api';
import { ApiError } from '@/lib/api/http';

const GRADE_LABELS = { office_admin: '管理責任者', office_member: '一般' } as const;

/**
 * 事務局管理者（緊急復旧 / implementation-spec §12 M11 / DECISIONS D-046）。
 * 管理責任者が誰もログインできなくなった場合の緊急対応。0人化ガードは維持される。
 */
export function SuperAdminOfficeAccountsPage(): ReactElement {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['superadmin-office-accounts'],
    queryFn: fetchOfficeAccountsForSuperAdmin,
  });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['superadmin-office-accounts'] });
  }

  async function onToggleGrade(account: OfficeAccount): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const next = account.office_grade === 'office_admin' ? 'office_member' : 'office_admin';
      await emergencyChangeGrade(account.user_id, next);
      setNotice(`${account.email} を${GRADE_LABELS[next]}に変更しました。`);
      await invalidate();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '変更に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onToggleStatus(account: OfficeAccount): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await emergencyChangeStatus(account.user_id, !account.is_active);
      setNotice(`${account.email} を${account.is_active ? '停止' : '有効化'}しました。`);
      await invalidate();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '変更に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onResetTotp(account: OfficeAccount): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await resetOfficeTotp(account.user_id);
      setNotice(`${account.email} の2要素認証をリセットしました。次回ログイン時に再設定が必要です。`);
      await invalidate();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'リセットに失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<OfficeAccount>[] = [
    { key: 'email', header: 'メール', primary: true, render: (a) => a.email },
    { key: 'display_name', header: '表示名', render: (a) => a.display_name ?? '—' },
    { key: 'grade', header: '権限区分', render: (a) => GRADE_LABELS[a.office_grade] },
    { key: 'status', header: '状態', render: (a) => (a.is_active ? '有効' : '停止中') },
    { key: 'totp', header: '2要素認証', render: (a) => (a.totp_enrolled ? '設定済み' : '未設定') },
  ];

  return (
    <>
      <PageHeader
        title="事務局管理者（緊急復旧）"
        description="事務局がログインできなくなった場合の緊急対応です。管理責任者を0人にする操作はできません。"
      />

      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      <ListTable<OfficeAccount>
        caption="事務局アカウント一覧"
        rows={query.data ?? []}
        columns={columns}
        rowKey={(a) => a.user_id}
        state={state}
        onRetry={() => void query.refetch()}
        rowActions={[
          { label: '権限区分を切替', onSelect: (a) => !busy && void onToggleGrade(a) },
          {
            label: '有効・停止を切替',
            onSelect: (a) => !busy && void onToggleStatus(a),
            destructive: true,
          },
          {
            label: '2要素認証をリセット',
            onSelect: (a) => !busy && void onResetTotp(a),
            destructive: true,
          },
        ]}
        emptyTitle="事務局アカウントがありません"
      />
    </>
  );
}
